import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Picker } from "emoji-mart";
import { useSelector} from 'react-redux';
import { GlobalState, MessageRatingType } from '../../../../store/types';
import cn from "classnames";
import {isMobile} from 'react-device-detect';

import Header from "./components/Header";
import Messages from "./components/Messages";
import Sender from "./components/Sender";
import Footer from "./components/Footer";
import StatusNotice from "./components/StatusNotice";
import BottomButtonBar from "./components/BottomButtonBar";

import { AnyFunction } from "../../../../utils/types";

import "./style.scss";
import MessageRatingCommentPopup from './components/MessageRatingCommentPopup';
import { putMessageRating } from 'src/store/dispatcher';
import { QuickReplyConfig } from 'src/utils/widgetSettings';
import { useAwayNow } from 'src/utils/useAwayNow';
import Loader from 'react-loader-spinner';

interface ISenderRef {
  onSelectEmoji: (event: any) => void;
}

type Props = {
  title: string;
  subtitle: string;
  senderPlaceHolder: string;
  showCloseButton: boolean;
  disabledInput: boolean;
  autofocus: boolean;
  className: string;
  sendMessage: AnyFunction;
  sendCustomComponent: AnyFunction;
  toggleChat: AnyFunction;
  profileAvatar?: string;
  titleAvatar?: string;
  onInteractiveButtonClicked?: AnyFunction;
  onHandleConversationScroll?: AnyFunction;
  onTextInputChange?: (event: any) => void;
  sendButtonAlt: string;
  showTimeStamp: boolean;
  emojis?: boolean;
  fullScreenMode: boolean;
  hideHeader?: boolean;
  /** True only when the welcome greeting is in the thread (IMBEE-8955). */
  greetingShown?: boolean;
  /** IMBEE-8069 (T86) Areas A and C. Absent for a channel without the feature. */
  quickReplyConfig?: QuickReplyConfig;
};

/** The away bubble's sent time, formatted the way the message list formats timestamps. */
const nowHhMm = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

function Conversation({
  title,
  subtitle,
  senderPlaceHolder,
  showCloseButton,
  disabledInput,
  autofocus,
  className,
  sendMessage,
  sendCustomComponent,
  toggleChat,
  quickReplyConfig,
  profileAvatar,
  titleAvatar,
  onInteractiveButtonClicked,
  onHandleConversationScroll,
  onTextInputChange,
  sendButtonAlt,
  showTimeStamp,
  emojis,
  fullScreenMode,
  hideHeader,
  greetingShown,
}: Props) {

  const parentUrl = useSelector((state: GlobalState) => state.responses.parentUrl);
  const messageRatingNegativeCommentPrompt = useSelector((state: GlobalState) => state.messages.ratingNegativeCommentPrompt);
  const messageRatingLoading = useSelector((state: GlobalState) => state.messages.ratingLoading);

  const [pickerOffset, setOffset] = useState(0);
  const senderRef = useRef<ISenderRef>(null!);
  const [pickerStatus, setPicket] = useState(false);
  const [showRatingCommentPopup, setShowRatingCommentPopup] = useState(false);
  const [ratingCommentPopupData, setRatingCommentPopupData] = useState<{type: MessageRatingType, messageId: string, prompt: string} | null>(null);

  const themeColor = useSelector((state: GlobalState) => state.responses.themeColor);

  /**
   * IMBEE-8069 UAT — Areas A and C stay out of the way until the visitor has a session.
   *
   * Both send the button's label as a visitor message, and a message can only be sent once the
   * contact form has been submitted and createSession has run. Clicked before that, the widget
   * POSTed /message with an EMPTY session_id: the button appeared to work, nothing reached the
   * platform, and the visitor got no error. Hiding the surfaces is the honest form of that.
   *
   * showContactForm is the state to read because it is the only state there is: the widget never
   * reads a contact-form config flag. `enableContactForm` does not appear anywhere in this repo —
   * `getWidget` does not request it and `renderContactForm()` renders the form for every
   * non-member visitor regardless of what the channel configured. So this gate means exactly
   * "hide while the form is up", and nothing more.
   *
   * It is false for member mode (App.tsx:454-458) and for a returning visitor whose session was
   * restored (App.tsx:630-633), and it flips false on submit — so the surfaces appear in all the
   * cases they should.
   *
   * What it does NOT do, and an earlier version of this comment wrongly claimed it did, is
   * recognise a channel whose operator turned the pre-chat form off. The widget shows that form
   * anyway — a pre-existing gap, not one this change introduced — and Areas A and C now wait
   * behind a form that arguably should not be there. Tracked as IMBEE-9559, which also carries
   * the product question: should the banner appear immediately on such a channel?
   */
  const showContactForm = useSelector((state: GlobalState) => state.behavior.showContactForm);

  const onSelectEmoji = (emoji) => {
    senderRef.current?.onSelectEmoji(emoji);
  };

  const togglePicker = () => {
    setPicket((prevPickerStatus) => !prevPickerStatus);
  };

  /**
   * IMBEE-8955 (T115) AC-7.2 / AC-7.3 — is the channel closed right now, re-evaluated when that
   * is due to change.
   *
   * The boundary timer, the self-rescheduling and the 32-bit setTimeout clamp all live in
   * useAwayNow, with its own tests. This component carried its own copy of them until the
   * pre-chat form needed the same behaviour outside this subtree and the hook was written;
   * keeping both left two implementations of one rule, free to drift, with only one under test.
   */
  const operatingHours = quickReplyConfig?.operatingHours;
  const away = useAwayNow(operatingHours);

  /**
   * The away bubble's sent time. A fixed moment, not a live clock — a ticking timestamp on a
   * message that never changes would be a lie about when the channel said it.
   *
   * The moment is the transition INTO away, not mount. Those differ for the case the timer above
   * exists to serve: a visitor who opens the widget at 15:00 while open and leaves the tab up
   * until the 18:00 edge. `schedule()` flips `away` there, and stamping at mount would date the
   * away message 15:00 — three hours before the channel said anything. That is the same lie,
   * reached from the other side.
   */
  const [awayTimestamp, setAwayTimestamp] = useState(() => nowHhMm());

  useEffect(() => {
    if (away) setAwayTimestamp(nowHhMm());
  }, [away]);

  // A visitor who ARRIVES outside hours is stamped at mount, deliberately, and that is not the
  // same defect as the transition case above.
  //
  // A chat bubble's timestamp means "when this reached you". The away bubble reaches an arriving
  // visitor now, so now is the honest answer — showing the 18:00 close time to someone opening
  // the widget at 23:00 would read as a message sent five hours ago that they somehow only just
  // received. The transition case differs because that visitor WAS watching: they saw the channel
  // open, so the bubble genuinely appeared at the edge and dating it from mount would predate the
  // event they witnessed.
  //
  // It is also not computable as written: widgetSettings exposes nextBoundary and no
  // previousBoundary, so the actual close time is not available on this path. Recorded so the
  // choice reads as a decision rather than the fallback it would otherwise look like.

  /**
   * AC-1.3 — with the feature off, nothing about operating hours may reach the visitor. `away` is
   * already false in that case, but the in-hours line would otherwise survive an operator turning
   * the feature back off after authoring one, so it is gated explicitly.
   */
  const responseTimeInHours = operatingHours?.enableOperatingHours
    ? operatingHours.responseTimeInHours
    : undefined;

  /**
   * IMBEE-8069 — the widget's height comes from the message list's fixed height, so chrome
   * rendered BESIDE the list would simply make the whole widget taller and push the composer
   * out of the frame. Measure what it occupies and hand it to Messages to subtract.
   *
   * `reservedHeight` is the chrome OUTSIDE the scroller. Since IMBEE-9725 the welcome banner is
   * no longer part of that: it renders inside .rcw-messages-container (see Messages), where it
   * costs the scroller nothing and there is nothing about it to measure. What remains is the
   * bottom button bar and the IMBEE-8955 closed-state notice, both genuinely pinned beside the
   * list.
   */
  const bottomBarRef = useRef<HTMLDivElement | null>(null);
  // IMBEE-8955 — the closed-state notice is pinned above the thread like the banner is, so it
  // has to be measured for the same reason: unmeasured, it makes the whole widget taller and
  // pushes the composer out of the frame exactly when a visitor most needs it (AC-4.3 keeps the
  // composer usable while closed). The in-hours line needs no ref — it lives inside the list.
  const awayNoticeRef = useRef<HTMLDivElement | null>(null);
  const [reservedHeight, setReservedHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () =>
      setReservedHeight(
        (bottomBarRef.current?.offsetHeight ?? 0) +
          (awayNoticeRef.current?.offsetHeight ?? 0),
      );
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    if (bottomBarRef.current) observer.observe(bottomBarRef.current);
    if (awayNoticeRef.current) observer.observe(awayNoticeRef.current);
    return () => observer.disconnect();
      // Three dependencies, three different reasons a measured surface appears or disappears:
      // quickReplyConfig decides whether the banner and bar exist at all; `away` mounts and
      // unmounts the away notice as the channel opens and closes; showContactForm mounts and
      // unmounts all of them together — and it is the only trigger the no-ResizeObserver
      // fallback path has. A stale observer on any of the three keeps reserving space for a node
      // that is no longer on screen.
    }, [quickReplyConfig, away, showContactForm]);

  const handlerSendMsn = (event, event2?, event3?) => {
    sendMessage(event, event2, event3);
    // console.log("event4: ", event4);
    if (pickerStatus) setPicket(false);
  };

  const promptMessageRatingComment = (messageId: string, type: MessageRatingType) => {
    setRatingCommentPopupData({
      type,
      messageId,
      prompt: messageRatingNegativeCommentPrompt,
    });
    setShowRatingCommentPopup(true);
  }

  const handleMessageRatingSubmit = (messageId: string, type: MessageRatingType, comment?: string) => {
    putMessageRating(messageId, type, comment);
  };

  const handleRating = (messageId: string, type: MessageRatingType) => {
    if(messageRatingNegativeCommentPrompt && type === MessageRatingType.NEGATIVE) {
      promptMessageRatingComment(messageId, type);
      return;
    }
    handleMessageRatingSubmit(messageId, type);
  }

  return (
    <div
      className={cn(
        fullScreenMode || isMobile ? 'rcw-conversation-container-fs' : 'rcw-conversation-container',
        className,
      )}
      aria-live="polite"
      onMouseLeave={(e) => {
        window.parent && parentUrl && window.parent.postMessage('isNotFocusing', parentUrl);
      }}
    >
      <Header
        title={title}
        subtitle={subtitle}
        toggleChat={toggleChat}
        showCloseButton={showCloseButton}
        titleAvatar={titleAvatar}
        fullScreenMode={fullScreenMode}
        operatingHoursClosed={operatingHours?.enableOperatingHours ? away : undefined}
        hideHeader={hideHeader}
      />
      {/*
        One component, two mount points, and `away` alone decides which — so the two states still
        cannot both appear, which is the property StatusNotice exists to guarantee.

        The closed-state panel is pinned here, above the thread: it explains why nothing is
        happening, so it must not scroll out of reach. The in-hours line goes inside the message
        list instead (see afterFirstMessage below), because the design places it as an answer to
        the greeting rather than a preamble to it.
      */}
      {away ? (
        <div ref={awayNoticeRef}>
          <StatusNotice
            away
            awayMessage={operatingHours?.awayMessage}
            reopenLine={operatingHours?.responseTimeReopen}
            profileAvatar={profileAvatar}
            timestamp={awayTimestamp}
          />
        </div>
      ) : null}

      <Messages
        profileAvatar={profileAvatar}
        showTimeStamp={showTimeStamp}
        onInteractiveButtonClicked={onInteractiveButtonClicked}
        onHandleConversationScroll={onHandleConversationScroll}
        fullScreenMode={fullScreenMode || isMobile}
        handleRating={handleRating}
        reservedHeight={reservedHeight}
        quickReplyConfig={quickReplyConfig}
        themeColor={themeColor}
        onBannerSelect={sendMessage}
        away={away}
        afterFirstMessage={
          // Anchored to the GREETING, not to array position. Messages renders this under
          // index 0, and index 0 is only the greeting when there was no history to load:
          // `shouldShowWelcomeMessage` injects it only at loadedCount === 0, while
          // getPreviousMessages() PREPENDS. For a returning visitor the line would otherwise
          // attach to whatever message happened to be oldest-loaded, and jump to a different
          // one each time scroll-to-top paged in more. The design calls this "an answer to
          // the greeting" — with no greeting there is nothing for it to answer.
          // `responseTimeInHours` is checked HERE rather than left to StatusNotice's internal
          // null-return: Messages wraps whatever it is given in `.rcw-after-first-message`,
          // so a component that renders null still leaves an empty positioned div under the
          // first message of every channel — including channels with the feature off, whose
          // DOM this change promises to leave untouched.
          away || !greetingShown || !responseTimeInHours?.trim() ? null : (
            <StatusNotice responseTimeInHours={responseTimeInHours} />
          )
        }
      />
      {emojis && pickerStatus && (
        <Picker
          style={{
            position: 'absolute',
            bottom: pickerOffset,
            left: '0',
            width: '100%',
          }}
          onSelect={onSelectEmoji}
          exclude={['flags']}
        />
      )}
      <div ref={bottomBarRef}>
        <BottomButtonBar
          enabled={!away && quickReplyConfig?.enableBottomButtons && !showContactForm}
          buttons={quickReplyConfig?.bottomButtons}
          onSelect={sendMessage}
        />
      </div>
      {/*
        IMBEE-8955 (T115) — the composer is hidden while the channel is closed, per the
        approved design: outside hours the visitor leaves details through the Pre-chat form,
        and an open message box would invite a message nobody is there to read.

        NOTE: this contradicts AC-4.3, which says the composer "stays as today" so a visitor
        can still send a normal message. Built to the design on an explicit instruction; the
        AC needs updating to match, or this needs reverting. Deferred to IMBEE-9922.

        HIDDEN, never unmounted, and that distinction is the whole point. The composer's text is
        uncontrolled DOM state — it lives only in a contentEditable's innerHTML, nowhere in React
        or Redux — so unmounting does not hide a draft, it destroys one. `away` flips LIVE here,
        from the boundary timer above, so a visitor typing when the schedule edge passes is an
        ordinary Tuesday evening rather than a corner case. Hiding keeps the draft and hands it
        back when the channel reopens.

        Independent of the AC-4.3 question above: if that decision restores the composer while
        closed, `hidden` is simply always false and nothing else here changes.
      */}
      <Sender
        hidden={away}
        sendMessage={handlerSendMsn}
        sendCustomComponent={sendCustomComponent}
        ref={senderRef}
        placeholder={senderPlaceHolder}
        disabledInput={disabledInput}
        autofocus={autofocus}
        onTextInputChange={onTextInputChange}
        buttonAlt={sendButtonAlt}
        onPressEmoji={togglePicker}
        onChangeSize={setOffset}
      />
      <Footer fullScreenMode={fullScreenMode || isMobile} />
      {showRatingCommentPopup && ratingCommentPopupData && (
        <MessageRatingCommentPopup
          onSubmit={handleMessageRatingSubmit}
          onClose={() => setShowRatingCommentPopup(false)}
          {...ratingCommentPopupData}
        />
      )}
      {messageRatingLoading && (
        <div className={`message-rating-loading-overlay`}>
          <Loader
            type="ThreeDots"
            color={`#${themeColor}`}
            height={100}
            width={100}
          />
        </div>
      )}
    </div>
  );
}

export default Conversation;
